import React, { memo, useContext, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import styles from '../../styles.module.scss';
import { CommentsContext } from '@components-clean/core/comments-side-bar/comments-context';
import CommentFormComponent from '@components-clean/core/comments-side-bar/components/comment-form-component';
import KebabMenu, { KebabMenuAction } from '@components-clean/core/comments-side-bar/components/kebab-menu';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { UserContext } from '@contexts/user-context';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

export interface Reply {
    id: string;
    createdAt: Date;
    createdBy: string;
    createdById: string;
    content: string;
    version: number;
}

export interface ReplyProps {
    reply: Reply;
    parentCommentOpen: boolean;
    onDeleteReply: (id: string) => void;
    onUpdateReply: (id: string, content: string) => Promise<boolean>;
    onDirtyStateUpdated: (id: string, isDirty: boolean) => void;
    disableActions: boolean;
}

const ReplyDetails = ({
    reply: { id, createdAt, createdBy, createdById, content },
    parentCommentOpen,
    onDeleteReply,
    onUpdateReply,
    onDirtyStateUpdated,
    disableActions,
}: ReplyProps) => {
    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const { user } = useContext(UserContext);

    const commentsContext = useContext(CommentsContext);
    const isAuthor = createdById === user?.id;
    const isAuthorActive = !commentsContext.inactiveOwners!.includes(createdById);

    const menuActions: KebabMenuAction[] = [
        {
            id: 1,
            label: t('comments:editReply'),
            icon: ['far', 'pen-to-square'],
            onClick: () => setIsEditing(true),
            disabled: !isAuthor || !parentCommentOpen || !commentsContext.writeEnabled || isEditing || disableActions,
            isRedIcon: false,
        },
        {
            id: 2,
            label: t('comments:deleteReply'),
            icon: ['fas', 'trash-xmark'],
            onClick: () => setShowDeleteDialog(true),
            disabled: !isAuthor || !parentCommentOpen || !commentsContext.writeEnabled || disableActions,
            isRedIcon: true,
        },
    ];

    useLayoutEffect(() => {
        setCanExpand((contentContainerRef.current?.scrollHeight ?? 0) > 60);
    }, [content]);

    useLayoutEffect(() => {
        if (!parentCommentOpen) {
            setIsEditing(false);
        }
    }, [parentCommentOpen]);

    return (
        <div data-testid="comment-reply-details">
            {showDeleteDialog && (
                <ConfirmationDialog
                    dataTestId="confirm-comment-delete"
                    title={t('comments:deleteReply')}
                    onSubmit={() => {
                        setShowDeleteDialog(false);
                        onDeleteReply(id);
                    }}
                    onClose={() => setShowDeleteDialog(false)}
                    primaryBtnLabel={t('common:delete')}
                >
                    {t('comments:deleteCommentReplyConfirmation')}
                </ConfirmationDialog>
            )}
            <div className={styles.replyDetails}>
                <div style={{ display: 'flex', flexGrow: 1 }}>
                    <FontAwesomeIcon
                        icon={['far', 'arrow-turn-down-right']}
                        className={cn(styles.replyIcon, { [styles.hidden]: isEditing })}
                    />
                    <div style={{ flexGrow: 1 }}>
                        {isEditing ? (
                            <div style={{ marginTop: '2px', marginRight: '6px' }}>
                                <CommentFormComponent
                                    defaultContent={content}
                                    disableSave={disableActions}
                                    onDirtyStateChanged={(isDirty) => onDirtyStateUpdated(id, isDirty)}
                                    isUpdate
                                    isReply
                                    onSave={(updatedContent) => onUpdateReply(id, updatedContent)}
                                    onClose={() => {
                                        setIsEditing(false);
                                        setExpanded(false);
                                        onDirtyStateUpdated(id, false);
                                    }}
                                />
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'inline-flex' }}>
                                    <div className={styles.boldText}>{createdBy}</div>
                                    {!isAuthorActive && (
                                        <div style={{ height: '16px', marginLeft: '5px' }}>
                                            <InactiveIcon dataTestId="inactive-icon" />
                                        </div>
                                    )}
                                    <div className={styles.replyCreatedDate}>
                                        {stringDateWithoutTimeFromUtc(createdAt, 'DD-MMM-YYYY')}
                                    </div>
                                </div>
                                <div
                                    className={cn(styles.replyContent, {
                                        [styles.contentTrimmed]: canExpand && !expanded,
                                    })}
                                    ref={contentContainerRef}
                                >
                                    {content}
                                </div>
                                {canExpand && (
                                    <button
                                        type="button"
                                        className={styles.showHideMoreLinkText}
                                        onClick={() => setExpanded((value) => !value)}
                                    >
                                        {expanded ? t('common:showLess') : t('common:showMore')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div>
                    <KebabMenu actions={menuActions} />
                </div>
            </div>
            <div className={styles.replySeparator} />
        </div>
    );
};

export default memo(ReplyDetails);
